import type { TaskFlag } from '@travel-planner/shared/schemas/tasks';
import { useSetFlag } from '@task-manager/entities/tasks';
import { Flag } from 'lucide-react';
import { useMutationToasts } from '@/shared/lib';
import { joinCss } from '@/shared/lib/css-utils';
import { Tooltip } from '@/shared/ui';
import css from './task-flag-toggle.module.scss';

type TaskFlagToggleProps = {
  taskId: string;
  current: TaskFlag | null | undefined;
};

/**
 * Legacy parity: two side-by-side icon buttons.
 *  - Yellow (Attention, flag=5): click while inactive sets flag; click
 *    while active unsets (flag=0).
 *  - Red (Urgent, flag=10): same toggle semantics for the urgent slot.
 *
 * Either flag being set "wins" — there's no merged state. Clicking the
 * other button while one is set replaces it.
 */
export const TaskFlagToggle = ({ taskId, current }: TaskFlagToggleProps) => {
  const mutation = useSetFlag();
  useMutationToasts(mutation, { success: 'Flag updated' });
  const isAttention = current === 5;
  const isUrgent = current === 10;

  const toggle = (target: TaskFlag) => {
    const next: TaskFlag = current === target ? 0 : target;
    mutation.mutate({ id: taskId, flag: next });
  };

  return (
    <div className={css.root}>
      <Tooltip content={isAttention ? 'Clear attention' : 'Flag attention'}>
        <button
          aria-label={isAttention ? 'Clear attention' : 'Flag attention'}
          aria-pressed={isAttention}
          className={joinCss(
            css.button,
            isAttention ? css.buttonIsAttentionActive : css.buttonIsAttention
          )}
          disabled={mutation.isPending}
          onClick={() => toggle(5)}
          type="button"
        >
          <Flag size={14} />
        </button>
      </Tooltip>
      <Tooltip content={isUrgent ? 'Clear urgent' : 'Flag urgent'}>
        <button
          aria-label={isUrgent ? 'Clear urgent' : 'Flag urgent'}
          aria-pressed={isUrgent}
          className={joinCss(
            css.button,
            isUrgent ? css.buttonIsUrgentActive : css.buttonIsUrgent
          )}
          disabled={mutation.isPending}
          onClick={() => toggle(10)}
          type="button"
        >
          <Flag size={14} />
        </button>
      </Tooltip>
    </div>
  );
};
